import { useEffect, useState } from "react";
import LoadingComponent from "../layout/share_components/LoadingComponent";
import agent from "../../api/agent";
import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Fab,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GetAppIcon from "@mui/icons-material/GetApp";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import DeleteConfirmationBox from "../layout/share_components/DeleteConfirmationBox";
import { Subscription } from "../models/Subscription/Subscription";
import { SubscriptionCreate } from "../models/Subscription/SubscriptionCreate";
import CreateSubscriptionForm from "../forms/Subscriptions/CreateSubscriptionForm";
import EditSubscriptionForm from "../forms/Subscriptions/EditSubscriptionForm";
import LicenseDetails from "../details/LicenseDetails";
import { checkTokenExpiration } from "../../api/tokenExpirationChecking";
import { showToast } from "../layout/share_components/ToastMessage";
import { Company } from "../models/Companies/Company";
import { Software } from "../models/Softwares/Software";

export default function SubscriptionDashboard() {
  const [subscriptionList, setSubscriptionList] = useState<Subscription[]>([]);
  const [filteredSubscriptionList, setFilteredSubscriptionList] = useState<
    Subscription[]
  >([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [createFormOpenState, setCreateFormOpenState] =
    useState<boolean>(false);
  const [editFormOpenState, setEditFormOpenState] = useState<boolean>(false);
  const [deleteDialogOpenState, setDeleteDialogOpenState] =
    useState<boolean>(false);
  const [selectedSubscription, setSelectedSubscription] =
    useState<SubscriptionCreate | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] =
    useState<number>(0);

  // For license detail dialog
  const [licenseDetailDialogOpenState, setLicenseDetailDialogOpenState] =
    useState<boolean>(false);
  const [softwarePrefixName, setSoftwarePrefixName] = useState<
    string | undefined
  >(undefined);
  // For getting the pax number of the subscription
  const [maxPax, setMaxPax] = useState<number>(0);

  const [selectedValues, setSelectedValues] = useState<string[]>([]);
  const [searchColumn, setSearchColumn] = useState<
    "companyName" | "softwareName" | "status"
  >("companyName");

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Authentication
  const [token, setToken] = useState<string>("");

  // For filteration dropdown option
  const [companyList, setCompanyList] = useState<Company[]>([]);
  const [softwareList, setSoftwareList] = useState<Software[]>([]);
  const statusOptions = ["Active", "Inactive", "End Service"];

  // For table sorting
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  const [orderBy, setOrderBy] = useState<
    "companyName" | "softwareName" | "validDate"
  >("companyName");

  const [exportLoading, setExportLoading] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // get the token from localstorage
        const tokenFLocal = localStorage.getItem("token");
        if (tokenFLocal !== null) {
          setToken(tokenFLocal);
          const subscriptionResponse =
            await agent.Subscription.list(tokenFLocal);
          setSubscriptionList(subscriptionResponse);
          setFilteredSubscriptionList(subscriptionResponse);

          // For the dropdown option(Company)
          const companyResponse = await agent.Company.list(tokenFLocal);
          setCompanyList(companyResponse);

          // For the dropdown option(Software)
          const softwareResponse = await agent.Softwares.list(tokenFLocal);
          setSoftwareList(softwareResponse);

          // close the loader after finish fetching data
          setLoading(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // function to open create form
  function handleCreateFormOpen() {
    setCreateFormOpenState(true);
  }

  function handleCreateOrEditFormClose() {
    setCreateFormOpenState(false);
    setEditFormOpenState(false);
    setSelectedSubscription(null);
  }

  function handleDeleteOnClick(softwareId: number) {
    setSelectedSubscriptionId(softwareId);
    setDeleteDialogOpenState(true);
  }

  function handleDeleteDialogClose() {
    setSelectedSubscriptionId(0);
    setDeleteDialogOpenState(false);
  }

  // function to open edit form(subscription)
  function handleEditFormOpen(subscriptionId: number) {
    const getSubscription = subscriptionList.find(
      (x) => x.subscriptionId === subscriptionId,
    );
    if (getSubscription) {
      // Map Subscription to SubscriptionCreate
      const SubscriptionCreate: SubscriptionCreate = {
        status: getSubscription.status,
        startDate: getSubscription.startDate,
        validDate: getSubscription.validDate,
        paxNo: getSubscription.paxNo,
        softwareId: getSubscription.softwareId,
        companyId: getSubscription.companyId,
        // Add other properties as needed
      };
      setSelectedSubscription(SubscriptionCreate);
      setSelectedSubscriptionId(subscriptionId);
      setEditFormOpenState(true);
    }
  }

  // Function to open license details Dialog (Eye Icon)
  function handleLicenseDetailsDialogOpen(
    subscriptionId: number,
    paxNumber: number | undefined,
    softwarePrefixName: string | undefined,
  ) {
    setSoftwarePrefixName(softwarePrefixName);
    setSelectedSubscriptionId(subscriptionId);
    if (paxNumber !== undefined) {
      setMaxPax(paxNumber);
    }
    setLicenseDetailDialogOpenState(true);
  }

  // Function to Close license details Dialog
  function handleLicenseDetailsDialogClose() {
    setSoftwarePrefixName(undefined);
    setSelectedSubscriptionId(0);
    setMaxPax(0);
    setLicenseDetailDialogOpenState(false);
  }

  // Function to create new Subscription record
  async function handleCreateSubmit(formData: SubscriptionCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.Subscription.create(formData, token).then(async () => {
        const updatedSubscription = await agent.Subscription.list(token);
        setSubscriptionList(updatedSubscription);
        handleCreateOrEditFormClose();
        showToast("Subscription Created Successfully", "success");
      });
    } catch (error) {
      console.error("Error Create Subscription Record:", error);
      showToast("Error Create Subscription Record", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to Update Subscription record
  async function handleEditSubmit(formData: SubscriptionCreate) {
    if (checkTokenExpiration(token)) {
      return;
    }
    setLoading(true);
    try {
      await agent.Subscription.update(
        selectedSubscriptionId,
        formData,
        token,
      ).then(async () => {
        const updatedSubscription = await agent.Subscription.list(token);
        setSubscriptionList(updatedSubscription);
        handleCreateOrEditFormClose();
        showToast("Subscription Edited Successfully", "success");
      });
    } catch (error) {
      console.error("Error Updating Subscription Record:", error);
      showToast("Error Updating Subscription Record", "error");
    } finally {
      setLoading(false);
    }
  }

  // Function to Delete Subscription record
  async function handleConfirmDelete() {
    if (checkTokenExpiration(token)) {
      return;
    }
    if (selectedSubscriptionId === 0) return;
    setLoading(true);
    try {
      await agent.Subscription.delete(selectedSubscriptionId, token).then(
        async () => {
          const updatedSubscription = await agent.Subscription.list(token);
          setSubscriptionList(updatedSubscription);
          handleDeleteDialogClose();
          showToast("Subscription Deleted Successfully", "success");
        },
      );
    } catch (error) {
      console.error("Error deleting Subscription Record:", error);
      showToast("Error Deleting Subscription Record", "error");
    } finally {
      setLoading(false);
      handleDeleteDialogClose(); //Reset all the delete form open state
    }
  }

  // Function for the search and filter
  // Get current options based on selected column
  const getCurrentOptions = () => {
    switch (searchColumn) {
      case "companyName":
        return companyList.map((company) => company.companyName);
      case "softwareName":
        return softwareList.map((software) => software.softwareName);
      case "status":
        return statusOptions;
      default:
        return [];
    }
  };

  const handleColumnChange = (event: any) => {
    setSearchColumn(event.target.value);
    setSelectedValues([]);
  };

  const handleValueChange = (event: any, newValues: string[]) => {
    setSelectedValues(newValues);
  };

  function applyFilters() {
    if (selectedValues.length === 0) {
      setFilteredSubscriptionList(subscriptionList);
      return;
    }
    const filtered = subscriptionList.filter((subscription) => {
      const columnValue = String(subscription[searchColumn]).toLowerCase();
      return selectedValues.some((selectedValue) =>
        columnValue.includes(selectedValue.toLowerCase()),
      );
    });

    setFilteredSubscriptionList(filtered);
  }

  // Function for pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // only triggered when user choose the option from the select
  useEffect(() => {
    applyFilters();
  }, [selectedValues, searchColumn, subscriptionList]);

  // For table sorting function
  const handleSort = (
    property: "companyName" | "softwareName" | "validDate",
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const getStringValue = (value: string | undefined): string => {
    return value?.toLowerCase() || "";
  };

  const sortedSubscriptionList = [...filteredSubscriptionList].sort((a, b) => {
    const isAsc = order === "asc";

    switch (orderBy) {
      case "companyName":
        const nameA = getStringValue(a.companyName);
        const nameB = getStringValue(b.companyName);
        return isAsc ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);

      case "softwareName":
        const emailA = getStringValue(a.softwareName);
        const emailB = getStringValue(b.softwareName);
        return isAsc
          ? emailA.localeCompare(emailB)
          : emailB.localeCompare(emailA);

      case "validDate":
        const dateA = a.validDate ? new Date(a.validDate).getTime() : 0;
        const dateB = b.validDate ? new Date(b.validDate).getTime() : 0;
        return isAsc ? dateA - dateB : dateB - dateA;

      default:
        return 0;
    }
  });

  // Function to export Subscription list to CSV file
  const handleExport = async () => {
    if (checkTokenExpiration(token)) {
      return;
    }
    setExportLoading(true);
    try {
      const response = await agent.Subscription.exportToCSV(token);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      let filename = `subscription-export-${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}.csv`;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showToast("Exported to CSV successfully", "success");
    } catch (error) {
      console.error("Error exporting to CSV:", error);
      showToast("Error exporting to CSV", "error");
    } finally {
      setExportLoading(false);
    }
  };

  // Trigger the loader when fetching data from database
  if (loading) return <LoadingComponent content="Loading Data..." />;
  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Subscription List</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Create New" followCursor>
            <Fab
              color="primary"
              onClick={() => handleCreateFormOpen()}
              size="small"
            >
              <AddIcon />
            </Fab>
          </Tooltip>

          <Tooltip title="Export to CSV" followCursor>
              <span>
                <Fab
                  color="success"
                  onClick={() => handleExport()}
                  size="small"
                  disabled={exportLoading}
                >
                  {exportLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <GetAppIcon />
                  )}
                </Fab>
              </span>
            </Tooltip>
        </Stack>
      </Stack>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Autocomplete
          multiple
          options={getCurrentOptions()}
          value={selectedValues}
          onChange={handleValueChange}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label="Filter"
              placeholder="Choose to filter"
            />
          )}
          renderTags={(value: string[], getTagProps) =>
            value.map((option: string, index: number) => (
              <Chip label={option} {...getTagProps({ index })} size="small" />
            ))
          }
          sx={{ minWidth: 300 }}
        />

        {/* For letting user to choose which column to be filter */}
        <FormControl sx={{ ml: 1 }}>
          <InputLabel>Filter by</InputLabel>
          <Select
            value={searchColumn}
            label="Filter by"
            onChange={handleColumnChange}
          >
            <MenuItem value="companyName">Company Name</MenuItem>
            <MenuItem value="softwareName">Software Name</MenuItem>
            <MenuItem value="status">Status</MenuItem>
            {/* <MenuItem value="frequency">Frequency</MenuItem> */}
          </Select>
        </FormControl>
      </Box>
      {filteredSubscriptionList.length === 0 ? (
        <Box sx={{ mt: 5 }}>
          <Divider>
            <Typography variant="button">No Record found</Typography>
          </Divider>
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "companyName"}
                    direction={orderBy === "companyName" ? order : "asc"}
                    onClick={() => handleSort("companyName")}
                  >
                    Company Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "softwareName"}
                    direction={orderBy === "softwareName" ? order : "asc"}
                    onClick={() => handleSort("softwareName")}
                  >
                    Software Name
                  </TableSortLabel>
                </TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Effective Date</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === "validDate"}
                    direction={orderBy === "validDate" ? order : "asc"}
                    onClick={() => handleSort("validDate")}
                  >
                    Valid Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>Pax</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedSubscriptionList
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((subscription) => (
                  <TableRow key={subscription.subscriptionId}>
                    <TableCell>{subscription.companyName}</TableCell>
                    <TableCell>{subscription.softwareName}</TableCell>
                    <TableCell>
                      {subscription.status === "Active" ? (
                        <Chip
                          label="Active"
                          color="success"
                          variant="outlined"
                        />
                      ) : subscription.status === "Disable" ? (
                        <Chip label="Disable" variant="outlined" />
                      ) : (
                        <Chip
                          label="End Service"
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </TableCell>
                    <TableCell>{subscription.startDate}</TableCell>
                    <TableCell>{subscription.validDate}</TableCell>
                    <TableCell>{subscription.paxNo}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        {/* Button for View License (In Dialog opening) */}
                        <Tooltip title="Modify / Edit License">
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleLicenseDetailsDialogOpen(
                                subscription.subscriptionId,
                                subscription.paxNo,
                                subscription.prefixName,
                              )
                            }
                          >
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                        {/* Button to open edit form */}
                        <Tooltip title="Edit Subscription Details">
                          <IconButton
                            color="success"
                            size="small"
                            onClick={() =>
                              handleEditFormOpen(subscription.subscriptionId)
                            }
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        {/* Button to delete the details */}
                        <Tooltip title="Delete Subscription">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() =>
                              handleDeleteOnClick(subscription.subscriptionId)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {/* Pagination Component (Company Table) */}
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]} // Options for rows per page
            component="div"
            count={filteredSubscriptionList.length} // Total number of rows
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              ".MuiTablePagination-toolbar": {
                paddingLeft: 2, // Adjust padding as needed
              },
            }}
          />
        </TableContainer>
      )}

      {/* --------------------- Subsciption MODEL------------------------------- */}
      {/* Model for Subsciption create form */}
      <Dialog
        open={createFormOpenState}
        onClose={handleCreateOrEditFormClose}
        maxWidth="lg" // Options: 'xs', 'sm', 'md', 'lg', 'xl' (md is larger than original)
        fullWidth // Ensures it takes the full width up to maxWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } }, // Optional: Customize backdrop
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Create New Subsciption
          </Typography>
        </DialogTitle>
        <DialogContent>
          <CreateSubscriptionForm
            closeForm={handleCreateOrEditFormClose}
            create={handleCreateSubmit}
          />
        </DialogContent>
      </Dialog>

      {/* Model for Subscription edit form */}
      <Dialog
        open={editFormOpenState}
        onClose={handleCreateOrEditFormClose}
        maxWidth="lg"
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: { xs: "90%", sm: "80%", md: "70%" }, // Responsive width
              maxWidth: "960px", // Matches 'md' breakpoint
              maxHeight: "80vh", // Limit height
              overflowY: "auto", // Scroll if needed
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle>
          <Typography variant="h6" component="h2">
            Edit Subscription Details
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedSubscription && (
            <EditSubscriptionForm
              selectedSubscription={selectedSubscription}
              closeForm={handleCreateOrEditFormClose}
              edit={handleEditSubmit}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog to show License Details */}
      <Dialog
        open={licenseDetailDialogOpenState}
        onClose={handleLicenseDetailsDialogClose}
        maxWidth={false}
        fullWidth
        slotProps={{
          backdrop: { sx: { bgcolor: "rgba(0, 0, 0, 0.5)" } },
          paper: {
            sx: {
              width: "95%", // Almost full viewport width
              maxWidth: "2000px", // Custom max width (adjust as needed)
              maxHeight: "80vh",
              overflowY: "auto",
              borderRadius: 2,
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            ml: 2,
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" component="h2">
            License Details
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleLicenseDetailsDialogClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              "&:hover": {
                color: (theme) => theme.palette.grey[700],
                backgroundColor: "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <LicenseDetails
            softwarePrefixName={softwarePrefixName}
            closeDialog={handleLicenseDetailsDialogClose}
            selectedSubscriptionId={selectedSubscriptionId}
            maxPaxNo={maxPax}
            userRole="Super Admin"
          />
        </DialogContent>
      </Dialog>

      <DeleteConfirmationBox
        open={deleteDialogOpenState}
        onClose={handleDeleteDialogClose}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}
